import { useEffect, useState, useRef } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
  useMap,
} from "@vis.gl/react-google-maps";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAP_ID = "DEMO_MAP_ID";

// Pin component — uses our custom branded SVG
const StorePin = ({ active }) => {
  const color = active ? "#FF5612" : "#FFFFFF";
  const stroke = active ? "#FFFFFF" : "#FF5612";
  return (
    <div
      style={{
        transform: active ? "scale(1.15)" : "scale(1)",
        transition: "transform 200ms ease",
        cursor: "pointer",
        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.4))",
      }}
    >
      <svg viewBox="0 0 40 48" width="38" height="46" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M20 0 C8.95 0 0 8.95 0 20 c0 12 20 28 20 28 s20-16 20-28 C40 8.95 31.05 0 20 0 z"
          fill={color}
          stroke={stroke}
          strokeWidth="2"
        />
        <circle cx="20" cy="19" r="6" fill={stroke} />
      </svg>
    </div>
  );
};

const UserPin = () => (
  <div
    style={{
      width: 18,
      height: 18,
      borderRadius: "50%",
      background: "#FF5612",
      border: "3px solid #FFFFFF",
      boxShadow: "0 0 0 4px rgba(255,86,18,0.25), 0 2px 8px rgba(0,0,0,0.6)",
    }}
  />
);

// Pans + zooms to the active store
const FlyTo = ({ position, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !position) return;
    map.panTo(position);
    map.setZoom(zoom ?? 13);
  }, [map, position, zoom]);
  return null;
};

// Draws a 50km radius circle around user location
const RadiusCircle = ({ center, radiusMeters }) => {
  const map = useMap();
  const circleRef = useRef(null);

  useEffect(() => {
    if (!map || !center || !window.google?.maps) return;

    if (circleRef.current) {
      circleRef.current.setMap(null);
    }

    circleRef.current = new window.google.maps.Circle({
      strokeColor: "#FF5612",
      strokeOpacity: 0.7,
      strokeWeight: 1.5,
      fillColor: "#FF5612",
      fillOpacity: 0.08,
      map,
      center,
      radius: radiusMeters,
      clickable: false,
    });

    return () => {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    };
  }, [map, center, radiusMeters]);

  return null;
};

const MapInner = ({ stores, userLocation, activeStoreId, onMarkerClick }) => {
  const [hoverStoreId, setHoverStoreId] = useState(null);
  const activeStore = stores?.find((s) => s.id === activeStoreId);
  const openStore =
    stores?.find((s) => s.id === activeStoreId) ||
    stores?.find((s) => s.id === hoverStoreId);

  const flyPos = activeStore
    ? { lat: activeStore.latitude, lng: activeStore.longitude }
    : userLocation
    ? { lat: userLocation.lat, lng: userLocation.lng }
    : null;

  return (
    <>
      {stores?.map((s) => (
        <AdvancedMarker
          key={s.id}
          position={{ lat: s.latitude, lng: s.longitude }}
          onClick={() => onMarkerClick && onMarkerClick(s.id)}
        >
          <StorePin active={s.id === activeStoreId} />
        </AdvancedMarker>
      ))}

      {userLocation && (
        <>
          <AdvancedMarker
            position={{ lat: userLocation.lat, lng: userLocation.lng }}
          >
            <UserPin />
          </AdvancedMarker>
          <RadiusCircle
            center={{ lat: userLocation.lat, lng: userLocation.lng }}
            radiusMeters={50000}
          />
        </>
      )}

      {openStore && (
        <InfoWindow
          position={{ lat: openStore.latitude, lng: openStore.longitude }}
          pixelOffset={[0, -44]}
          onCloseClick={() => onMarkerClick && onMarkerClick(null)}
        >
          <div style={{ minWidth: 200, padding: "4px 6px" }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#FF5612",
                marginBottom: 4,
              }}
            >
              {openStore.city}
            </div>
            <div
              style={{
                fontFamily: "'Onest', sans-serif",
                fontWeight: 700,
                fontSize: 15,
                color: "#0A0A0A",
                lineHeight: 1.2,
                marginBottom: 6,
              }}
            >
              {openStore.name}
            </div>
            <div style={{ fontSize: 12, color: "#525252", lineHeight: 1.4, marginBottom: 4 }}>
              {openStore.address}
            </div>
            <div style={{ fontSize: 12, color: "#0A0A0A", fontWeight: 500 }}>
              {openStore.phone}
            </div>
            {openStore.distance_km != null && (
              <div
                style={{
                  display: "inline-block",
                  marginTop: 6,
                  background: "#FF5612",
                  color: "#FFF",
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 999,
                }}
              >
                {openStore.distance_km} km away
              </div>
            )}
          </div>
        </InfoWindow>
      )}

      <FlyTo position={flyPos} zoom={activeStore ? 13 : userLocation ? 10 : 5} />
    </>
  );
};

export const MapView = ({ stores, userLocation, activeStoreId, onMarkerClick }) => {
  const defaultCenter = userLocation
    ? { lat: userLocation.lat, lng: userLocation.lng }
    : stores && stores.length > 0
    ? { lat: stores[0].latitude, lng: stores[0].longitude }
    : { lat: 20.5937, lng: 78.9629 };

  if (!API_KEY) {
    return (
      <div
        data-testid="map-view"
        className="h-full w-full flex items-center justify-center bg-zinc-900 text-zinc-400 text-sm p-6 text-center"
      >
        Google Maps API key not configured. Set REACT_APP_GOOGLE_MAPS_API_KEY in .env.
      </div>
    );
  }

  return (
    <div className="h-full w-full" data-testid="map-view">
      <APIProvider apiKey={API_KEY}>
        <Map
          defaultCenter={defaultCenter}
          defaultZoom={userLocation ? 10 : 5}
          mapId={MAP_ID}
          gestureHandling="greedy"
          disableDefaultUI={false}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          zoomControl
          style={{ width: "100%", height: "100%" }}
        >
          <MapInner
            stores={stores}
            userLocation={userLocation}
            activeStoreId={activeStoreId}
            onMarkerClick={onMarkerClick}
          />
        </Map>
      </APIProvider>
    </div>
  );
};

export default MapView;
